import React, { useState, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import { connect } from 'react-redux';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { closeImportEsns } from '../../reducers/DialogsReducer';
import { handleError } from '../../reducers/ErrorReducer';
import { showSpinner, hideSpinner } from '../../reducers/UiReducer';
import { notify, closeSnackbar } from '../../reducers/NotifierReducer';
import CSVIcon from '@material-ui/icons/Description';
import Tooltip from '../../components/Tooltip';
import TextField from '@material-ui/core/TextField';
import { Alert, AlertTitle } from '@material-ui/lab';
import { asyncForEach } from '../../utils/functions';
import { zonesChanged } from '../../reducers/DataChangeReducer';
import { parseString } from 'xml2js';
import { getPathCenter, strToPath } from 'utils/mapFunctions';
import { saveZone } from 'reducers/ZonesReducer';
import { sortObjArr } from '../../utils/functions';
import f from './geofenceHelpers';

function getFormStyle(minWidth, maxWidth) {
  return {
    maxWidth: maxWidth,
    flexBasis: minWidth,
    minWidth: minWidth,
    flexGrow: 1,
    margin: `0 4px 12px`,
  };
}

const useStyles = makeStyles(theme => ({
  warning: {
    marginBottom: theme.spacing(1),
  },
  error: {
    marginBottom: theme.spacing(1),
  },
  form: {
    margin: `0 -${theme.spacing(0.5)}px`,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  agency: {
    ...getFormStyle(200, 300),
  },
  importBtn: {
    margin: `0 ${theme.spacing(0.5)}px`,
    '& svg': {
      marginRight: theme.spacing(1),
    },
  },
  actions: {
    textAlign: 'right',
    marginBottom: 20,
    '& button': {
      marginLeft: theme.spacing(1),
    },
  },
  input: {
    display: 'none',
  },
}));

function ImportKlmZones(props) {
  const classes = useStyles();
  const { Agencies } = props.dictionary;
  const [rawData, setRawData] = useState([]);
  const [mode, setMode] = useState('import'); // import, edit, settings
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [tag, setTag] = useState('Zone_Code');
  const kmlFileRef = useRef(null);

  const handleChangeKMLFile = ev => {
    setErrors([]);
    setWarnings([]);
    const file = ev?.target?.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = ev => {
      parseKLMString(ev.target.result);
    };
    reader.onerror = () => {
      props.notify('Error, file not loaded', 'error');
    };
    reader.readAsText(file);
  };

  const parseKLMString = kml => {
    const getDataErrors = rows => {
      return f.checkMandatoryProps(rows, [tag, 'polygon']);
    };

    const prepSaveData = async data => {
      // Merge multiple ESNs as single path
      const uniqueEsns = [];
      await asyncForEach(data, async el => {
        if (!uniqueEsns.find(e => e[tag] === el[tag])) {
          const duplEsns = data.filter(e => e[tag] === el[tag]);
          if (duplEsns.length === 1) {
            uniqueEsns.push(el);
          } else {
            const polygon = await f.joinPolygons(duplEsns.map(e => e.polygon));
            if (polygon) {
              uniqueEsns.push({ ...el, polygon });
            } else {
              props.notify(
                `Polygons for ${el[tag]} were not merged propersly. Please check for possible intersections.`
              );
            }
          }
        }
      });

      const saveData = uniqueEsns.map((esn, idx) => ({
        id: idx + 1,
        ZoneCode: esn[tag],
        SurfaceText: esn.polygon,
        Color: esn.PolyColor ? esn.PolyColor : 'Blue',
        AgencyID: null,
        agency: null,
      }));
      return saveData;
    };

    parseString(kml, async (err, result) => {
      let data = [];
      try {
        const parsed = await f.parseXMLResult(result, tag);
        if (parsed.error) {
          props.notify(parsed.error, 'error');
          return;
        } else {
          data = parsed.data;
        }
      } catch (err) {
        props.notify('Error parsing KML data', 'error');
        return;
      }
      const dataErrors = getDataErrors(data);
      setErrors(dataErrors);
      if (dataErrors.length) {
        kmlFileRef.current.value = '';
        return;
      }
      const saveData = await prepSaveData(data);
      setRawData(sortObjArr(saveData, 'ZoneCode'));
      setMode('edit');
    });
  };

  const cleanLoadData = () => {
    setMode('import');
    setWarnings([]);
    setRawData([]);
  };

  const convertEsnsToZones = () => {
    const zones = [];
    rawData.forEach(esn => {
      const { ZoneCode, Color, SurfaceText, AgencyID } = esn;
      const path = strToPath(SurfaceText);
      const center = getPathCenter(path);
      const rec = {
        AgencyID,
        ZoneCode,
        Color,
        AttachToEvent: true,
        Description: '',
        SurfaceText,
        Code: 'Zone',
        center,
        path,
      };
      zones.push(rec);
    });
    const filteredZones = zones.filter(zone => zone.AgencyID);
    return filteredZones;
  };

  const importData = async () => {
    props.showSpinner();
    const zones = convertEsnsToZones();
    await f.removeNameDuplicates(zones);
    const errors = [];
    try {
      await asyncForEach(zones, async zone => {
        const result = await saveZone(zone);
        const isOk = result?.valid;
        if (!isOk) {
          errors.push(
            `Error, Invalid geofence ${zone.ZoneCode} from ${zone.AgencyID}. Check for possible polygon intersections.`
          );
        }
      });
      props.notify('Import successful', 'success');
    } catch (err) {
      props.handleError(err, 'Import file error.');
    }
    setErrors(errors);
    props.hideSpinner();
    props.zonesChanged();
    cleanLoadData();
  };

  const renderInputOptions = () => {
    return (
      <div className={classes.form}>
        <Tooltip title="ZoneCode Tag from XML file">
          <TextField
            label="ZonCode Tag"
            value={tag}
            onChange={ev => setTag(ev.target.value)}
            className={classes.agency}
            variant="outlined"
            size="small"
          />
        </Tooltip>
        <Tooltip title="Import KML ESNs">
          <div className={classes.importBtn}>
            <input
              accept=".kml"
              className={classes.input}
              id="import-esn-kml-file"
              multiple
              type="file"
              onChange={handleChangeKMLFile}
              ref={kmlFileRef}
            />
            <label htmlFor="import-esn-kml-file">
              <Button color="primary" component="span" variant="outlined">
                <CSVIcon />
                Import KML
              </Button>
            </label>
          </div>
        </Tooltip>
      </div>
    );
  };

  const renderEditAgencies = () => {
    const renderSettings = () => {
      const onChange = (a, agency) => {
        const newRecord = { ...a, agency, AgencyID: agency?.AgencyID || null };
        const index = rawData.findIndex(r => r.id === a.id);
        if (index !== -1) {
          rawData[index] = newRecord;
          setRawData([...rawData]);
        }
      };

      return (
        <div className={classes.form}>
          {rawData.map(a => (
            <Autocomplete
              className={classes.agency}
              options={Agencies}
              value={a.agency}
              onChange={(ev, agency) => onChange(a, agency)}
              key={a.id}
              getOptionLabel={option => option.AgencyID}
              renderInput={params => (
                <TextField {...params} label={a.ZoneCode} variant="outlined" size="small" />
              )}
            />
          ))}
          <div className={classes.agency}></div>
          <div className={classes.agency}></div>
          <div className={classes.agency}></div>
        </div>
      );
    };

    return (
      <>
        <h6>Agency Settings</h6>
        {renderSettings()}
        <div className={classes.actions}>
          <Button
            color="primary"
            variant="outlined"
            onClick={importData}
            // disabled={!importValid()}
          >
            Import
          </Button>
          <Button color="primary" variant="contained" onClick={cleanLoadData}>
            Cancel
          </Button>
        </div>
      </>
    );
  };

  return (
    <>
      {mode === 'import' && renderInputOptions()}
      {mode === 'edit' && renderEditAgencies()}
      {errors.length > 0 && (
        <Alert severity="error" className={classes.error}>
          <AlertTitle>Error processing the file</AlertTitle>
          {errors.map((err, idx) => (
            <p key={idx}>{err}</p>
          ))}
        </Alert>
      )}
      {warnings.length > 0 && (
        <Alert severity="warning" className={classes.warning}>
          <AlertTitle>Warning:</AlertTitle>
          {warnings.map((err, idx) => (
            <p key={idx}>{err}</p>
          ))}
        </Alert>
      )}
    </>
  );
}

const mapStateToProps = state => {
  return {
    dictionary: state.dictionary,
  };
};

export default connect(mapStateToProps, {
  closeImportEsns,
  notify,
  showSpinner,
  hideSpinner,
  handleError,
  closeSnackbar,
  zonesChanged,
})(ImportKlmZones);
